import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ClipboardList, Search } from 'lucide-react';
import Card from '../../components/Card';
import Input from '../../components/Input';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { apiService } from '../../services/apiService';
import { renderCell, coalesce, renderCellDate } from '../../utils/helpers';

const ExtinguisherInventoryView = () => {
  const { user } = useAuth();
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const { error: showError } = useToast();

  useEffect(() => {
    loadInventory();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [inventory, searchQuery]);

  const loadInventory = async () => {
    try {
      setLoading(true);
      // Fetch only data for this client
      const data = await apiService.getExtinguisherInventory(user?.id, null, null);
      setInventory(data);
    } catch (error) {
      showError('Erreur lors du chargement');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = inventory;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.inventoryNumber?.toLowerCase().includes(query) ||
          item.serialNumber?.toLowerCase().includes(query) ||
          item.detailedLocation?.toLowerCase().includes(query)
      );
    }

    setFilteredInventory(filtered);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <ClipboardList className="w-8 h-8 text-red-600" />
          État des Extincteurs
        </h1>
        <p className="text-gray-600 mt-1">
          Inventaire détaillé des extincteurs par emplacement
        </p>
      </div>

      {/* Search */}
      <Card>
        <Input
          placeholder="Rechercher par N° inventaire, N° série..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          icon={Search}
        />
      </Card>

      {/* Table Structure - Read Only */}
      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">N°</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">N°/Inventaire</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">N°/Série</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Emplacement</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Type</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Date de contrôle</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Prochain contrôle</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Observation</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInventory.map((item, index) => {
                return (
                  <motion.tr
                    key={item.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-3 py-3 text-sm text-center text-gray-900">{index + 1}</td>
                    <td className="px-3 py-3 text-sm text-center text-gray-900 font-medium">
                      {renderCell(item.inventoryNumber)}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-700">{renderCell(item.serialNumber)}</td>
                    <td className="px-3 py-3 text-sm text-gray-700">
                      {renderCell(coalesce(item.detailedLocation, item.location))}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-700">
                      {renderCell(coalesce(item.detailedType, item.extinguisherType, item.type))}
                    </td>
                    <td className="px-3 py-3 text-sm text-center text-gray-700">
                      {renderCellDate(item.lastMaintenanceDate)}
                    </td>
                    <td className="px-3 py-3 text-sm text-center text-gray-700">
                      {renderCellDate(item.nextMaintenanceDate)}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-700 font-medium">
                      {renderCell(item.observation)}
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>

          {filteredInventory.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <ClipboardList className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Aucun extincteur trouvé
              </h3>
              <p className="text-gray-600">
                Aucun extincteur ne correspond aux critères de recherche
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-gray-900">{filteredInventory.length}</p>
            <p className="text-sm text-gray-600 mt-1">Total</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">
              {filteredInventory.filter(item => {
                const next = item.nextMaintenanceDate ? new Date(item.nextMaintenanceDate) : null;
                return next && (next - new Date()) / (1000 * 60 * 60 * 24) > 30;
              }).length}
            </p>
            <p className="text-sm text-gray-600 mt-1">OK</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-amber-600">
              {filteredInventory.filter(item => {
                const next = item.nextMaintenanceDate ? new Date(item.nextMaintenanceDate) : null;
                if (!next) return false;
                const days = (next - new Date()) / (1000 * 60 * 60 * 24);
                return days > 0 && days <= 10;
              }).length}
            </p>
            <p className="text-sm text-gray-600 mt-1">Urgent</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-red-600">
              {filteredInventory.filter(item => {
                const next = item.nextMaintenanceDate ? new Date(item.nextMaintenanceDate) : null;
                return next && next < new Date();
              }).length}
            </p>
            <p className="text-sm text-gray-600 mt-1">Expirés</p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ExtinguisherInventoryView;

